import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Brand } from "../components/Logo";
import { OuiNon, UrgenceChoice } from "../components/FicheParts";

const today = () => new Date().toLocaleDateString("sv-SE");
const VIDE = {
  nom: "", prenom: "", date_naissance: "", adresse: "", cp_ville: "",
  r1_lien: "", r1_nom: "", r1_prenom: "", r1_tel: "", r1_adresse: "",
  r2_lien: "", r2_nom: "", r2_prenom: "", r2_tel: "", r2_adresse: "",
  sante_probleme: "", sante_details: "", traitement: "", traitement_details: "", allergies: "", allergies_details: "", autres_infos: "",
  medecin_nom: "", medecin_tel: "",
  urgence_choix: "", urgence_nom: "", urgence_lien: "", urgence_tel: "", urgence_tel2: "",
  confirme: false, date_fiche: today(),
};
export const TEL = /^[+\d][\d\s./-]{5,}$/;
const input = "w-full min-w-0 rounded-xl border bg-ink px-3 py-2.5 text-base text-chalk outline-none transition-colors focus:border-lime";
const lbl = "mb-1 block font-mono text-[11px] uppercase tracking-[0.15em] text-dim";

const valider = (f) => {
  const e = {};
  const req = (k) => { if (!f[k].trim()) e[k] = "Champ obligatoire"; };
  const tel = (k, obligatoire) => { if (f[k].trim() && !TEL.test(f[k].trim())) e[k] = "Numéro invalide"; else if (obligatoire) req(k); };
  ["nom", "prenom", "date_naissance", "r1_lien", "r1_nom", "r1_prenom"].forEach(req);
  tel("r1_tel", true); tel("r2_tel", false); tel("medecin_tel", false); tel("urgence_tel2", false);
  if (f.sante_probleme === "oui") req("sante_details");
  if (f.traitement === "oui") req("traitement_details");
  if (f.allergies === "oui") req("allergies_details");
  if (!f.urgence_choix) e.urgence_choix = "Choisissez le contact d'urgence";
  if (f.urgence_choix === "resp2" && !(f.r2_nom.trim() && f.r2_tel.trim())) e.urgence_choix = "Renseignez le nom et le téléphone du responsable 2";
  if (f.urgence_choix === "autre") { req("urgence_nom"); req("urgence_lien"); tel("urgence_tel", true); }
  if (!f.confirme) e.confirme = "Merci de cocher cette case";
  if (!f.date_fiche) e.date_fiche = "Champ obligatoire";
  return e;
};

export const Section = ({ n, title, children, tone = "lime", testid }) => (
  <section data-testid={testid} className={`rounded-[24px] border bg-pitch/80 p-4 sm:p-6 ${tone === "signal" ? "border-signal/60" : "border-line"}`}>
    <h2 className={`font-display text-xl uppercase sm:text-2xl ${tone === "signal" ? "text-signal" : "text-lime"}`}><span className="mr-2 font-mono text-sm text-dim">{n}.</span>{title}</h2>
    <div className="mt-4 grid gap-3 sm:grid-cols-2">{children}</div>
  </section>
);

export const Champ = ({ k, label, f, set, err, type = "text", required, className = "", placeholder, readOnly }) => (
  <label className={`block ${className}`}><span className={lbl}>{label}{required && <span className="text-signal"> *</span>}</span>
    <input data-testid={`fiche-${k.replace(/_/g, "-")}`} type={type} value={f[k]} placeholder={placeholder} readOnly={readOnly} onChange={(e) => set(k)(e.target.value)} aria-invalid={!!err[k]}
      className={`${input} ${err[k] ? "border-signal" : "border-line"} ${type === "date" ? "[color-scheme:dark]" : ""} ${readOnly ? "cursor-not-allowed opacity-70" : ""}`} />
    {err[k] && <span data-testid={`fiche-${k.replace(/_/g, "-")}-error`} className="mt-1 block text-xs text-signal">{err[k]}</span>}
  </label>
);

const LIENS = ["Papa", "Maman", "Autre"];
const Lien = ({ k, f, set, err, required }) => {
  const mode = LIENS.slice(0, 2).includes(f[k]) ? f[k] : f[k] || f[`${k}_autre`] ? "Autre" : "";
  return (
    <div className="sm:col-span-2">
      <span className={lbl}>Lien avec l'enfant{required && <span className="text-signal"> *</span>}</span>
      <div className="grid grid-cols-3 gap-2">
        {LIENS.map((l) => (
          <button key={l} type="button" data-testid={`fiche-${k.replace(/_/g, "-")}-${l.toLowerCase()}`} aria-pressed={mode === l}
            onClick={() => { set(`${k}_autre`)(l === "Autre"); set(k)(l === "Autre" ? "" : l); }}
            className={`rounded-full border px-2 py-2 text-sm font-semibold transition-colors ${mode === l ? "border-lime bg-lime text-ink" : "border-line bg-ink text-chalk hover:border-lime"}`}>{l}</button>
        ))}
      </div>
      {mode === "Autre" && <input data-testid={`fiche-${k.replace(/_/g, "-")}-autre`} value={f[k]} maxLength={40} onChange={(e) => set(k)(e.target.value)} placeholder="Préciser (ex. : grand-mère, tuteur)" className={`${input} mt-2 border-line`} />}
      {err[k] && <span data-testid={`fiche-${k.replace(/_/g, "-")}-error`} className="mt-1 block text-xs text-signal">{err[k]}</span>}
    </div>
  );
};

const Responsable = ({ n, f, set, err, required }) => (
  <Section n={n + 1} title={`Parent / Responsable ${n}`} testid={`fiche-section-r${n}`}>
    <Lien k={`r${n}_lien`} f={f} set={set} err={err} required={required} />
    <Champ k={`r${n}_nom`} label="Nom" f={f} set={set} err={err} required={required} />
    <Champ k={`r${n}_prenom`} label="Prénom" f={f} set={set} err={err} required={required} />
    <Champ k={`r${n}_tel`} label="Téléphone" type="tel" f={f} set={set} err={err} required={required} />
    <Champ k={`r${n}_adresse`} label="Adresse" f={f} set={set} err={err} />
  </Section>
);

export const QUESTIONS = [["sante_probleme", "sante_details", "Problème de santé", "Si oui, précisions :"], ["traitement", "traitement_details", "Traitement médical", "Si oui, précisions :"], ["allergies", "allergies_details", "Allergies connues", "Si oui, lesquelles :"]];

const Merci = ({ onAutre }) => (
  <div data-testid="fiche-success" className="rounded-[24px] border border-lime/60 bg-pitch p-6 text-center">
    <CheckCircle2 className="mx-auto h-12 w-12 text-lime" />
    <h2 className="mt-3 font-display text-3xl uppercase text-chalk">Merci !</h2>
    <p className="mt-2 text-chalk/90">La fiche a bien été envoyée au staff. Les informations seront ajoutées au dossier de votre enfant.</p>
    <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-center">
      <button type="button" data-testid="fiche-another" onClick={onAutre} className="rounded-full border border-line px-5 py-2.5 text-sm text-chalk hover:border-lime">Remplir une fiche pour un autre enfant</button>
      <Link to="/" data-testid="fiche-home" className="rounded-full bg-lime px-5 py-2.5 text-sm font-semibold text-ink">Retour au site</Link>
    </div>
  </div>
);

export default function Fiche() {
  const [f, setF] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    return { ...VIDE, nom: (q.get("nom") ?? "").slice(0, 60), prenom: (q.get("prenom") ?? "").slice(0, 60) };
  });
  const [err, setErr] = useState({});
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [serverErr, setServerErr] = useState("");
  const [token] = useState(() => new URLSearchParams(window.location.search).get("t") ?? "");
  const [lien, setLien] = useState(token ? "chargement" : "");
  useEffect(() => {
    if (!token) return;
    api.get(`/fiches/lien/${encodeURIComponent(token)}`).then((r) => {
      const { deja_remplie: deja, ...d } = r.data ?? {};
      setF((p) => ({ ...p, ...Object.fromEntries(Object.entries(d).filter(([k, v]) => k in VIDE && typeof v === "string")) }));
      setLien(deja ? "modification" : "perso");
    }).catch((x) => { setLien("invalide"); setServerErr(x.response?.data?.detail || "Lien de fiche invalide. Demandez un nouveau lien au staff."); });
  }, [token]);
  const set = (k) => (v) => { setF((p) => ({ ...p, [k]: v })); setErr((e) => ({ ...e, [k]: undefined })); };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = valider(f);
    setErr(e);
    if (Object.keys(e).length) {
      toast.error("Certains champs sont à compléter");
      setTimeout(() => document.querySelector("[aria-invalid=true], [data-error=true]")?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
      return;
    }
    setBusy(true);
    setServerErr("");
    try {
      const body = { ...Object.fromEntries(Object.entries(f).filter(([k]) => !k.endsWith("_autre"))), token };
      await api.post("/fiches", body);
      setOk(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (x) {
      const d = x.response?.data?.detail;
      const msg = Array.isArray(d) ? d[0]?.msg?.replace(/^Value error, /, "") : d || "Envoi impossible, réessayez";
      setServerErr(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-ink">
      <header className="sticky top-0 z-50 border-b border-chalk/5 bg-ink/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl items-center px-4 py-2.5 sm:px-6"><Brand /></div>
      </header>
      <main data-testid="fiche-page" className="mx-auto max-w-3xl px-4 pb-20 pt-8 sm:px-6">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-lime">À remplir par les parents</p>
        <h1 data-testid="fiche-title" className="mt-3 font-display text-4xl uppercase leading-[0.95] text-chalk sm:text-5xl">Fiche de renseignements - Enfant</h1>
        <p className="mt-3 text-sm text-dim">Les champs marqués <span className="text-signal">*</span> sont obligatoires. Ces informations restent confidentielles et ne sont visibles que par le staff.</p>
        {lien === "modification" && !ok && <p data-testid="fiche-mode-modification" className="mt-4 rounded-xl border border-lime/60 bg-lime/10 px-4 py-3 text-sm text-chalk">La fiche de <strong>{f.prenom} {f.nom.toUpperCase()}</strong> a déjà été envoyée. Les informations connues sont pré-remplies : corrigez ce qui a changé, puis renvoyez la fiche.</p>}
        {lien === "perso" && !ok && <p data-testid="fiche-mode-perso" className="mt-4 rounded-xl border border-line bg-pitch px-4 py-3 text-sm text-chalk">Fiche de <strong>{f.prenom} {f.nom.toUpperCase()}</strong> : les informations déjà connues du club sont pré-remplies, complétez le reste.</p>}
        {lien === "invalide" && <p data-testid="fiche-lien-invalide" className="mt-4 rounded-xl border border-signal bg-signal/10 px-4 py-3 text-sm text-chalk">{serverErr}</p>}
        {lien === "chargement" && <p className="mt-6 text-dim">Chargement de la fiche…</p>}
        {lien === "chargement" || lien === "invalide" ? null : ok ? <div className="mt-8"><Merci onAutre={() => { if (token) { window.location.assign("/fiche"); return; } setF({ ...VIDE, date_fiche: today() }); setOk(false); }} /></div> : (
          <form onSubmit={submit} noValidate className="mt-8 space-y-5">
            <Section n={1} title="Renseignements de l'enfant" testid="fiche-section-enfant">
              <Champ k="nom" label="Nom" f={f} set={set} err={err} required readOnly={!!token} />
              <Champ k="prenom" label="Prénom" f={f} set={set} err={err} required readOnly={!!token} />
              <Champ k="date_naissance" label="Date de naissance" type="date" f={f} set={set} err={err} required />
              <Champ k="adresse" label="Adresse" f={f} set={set} err={err} />
              <Champ k="cp_ville" label="Code postal / Ville" f={f} set={set} err={err} className="sm:col-span-2" />
            </Section>
            <Responsable n={1} f={f} set={set} err={err} required />
            <Responsable n={2} f={f} set={set} err={err} />
            <Section n={4} title="Informations médicales" testid="fiche-section-medical">
              {QUESTIONS.map(([k, d, label, ph]) => (
                <div key={k} className="rounded-xl border border-line p-3 sm:col-span-2">
                  <OuiNon label={label} value={f[k]} onChange={set(k)} testid={`fiche-${k.replace(/_/g, "-")}`} />
                  {f[k] === "oui" && <Champ k={d} label={ph} f={f} set={set} err={err} required className="mt-3" />}
                </div>
              ))}
              <label className="block sm:col-span-2"><span className={lbl}>Autres informations</span>
                <textarea data-testid="fiche-autres-infos" rows={3} maxLength={1000} value={f.autres_infos} onChange={(e) => set("autres_infos")(e.target.value)} className={`${input} resize-none border-line`} />
              </label>
            </Section>
            <Section n={5} title="Médecin traitant" testid="fiche-section-medecin">
              <Champ k="medecin_nom" label="Nom et prénom" f={f} set={set} err={err} />
              <Champ k="medecin_tel" label="Téléphone" type="tel" f={f} set={set} err={err} />
            </Section>
            <Section n={6} title="Contact en cas d'urgence" tone="signal" testid="fiche-section-urgence">
              <div className="sm:col-span-2" data-error={!!err.urgence_choix}>
                <span className={lbl}>Qui prévenir ?<span className="text-signal"> *</span></span>
                <UrgenceChoice value={f.urgence_choix} onChange={set("urgence_choix")} testid="fiche-urgence"
                  r1={{ lien: f.r1_lien, nom: [f.r1_prenom, f.r1_nom].filter(Boolean).join(" "), tel: f.r1_tel }}
                  r2={{ lien: f.r2_lien, nom: [f.r2_prenom, f.r2_nom].filter(Boolean).join(" "), tel: f.r2_tel }} />
                {err.urgence_choix && <span data-testid="fiche-urgence-choix-error" className="mt-1 block text-xs text-signal">{err.urgence_choix}</span>}
              </div>
              {f.urgence_choix === "autre" && <>
                <Champ k="urgence_nom" label="Nom et prénom" f={f} set={set} err={err} required />
                <Champ k="urgence_lien" label="Lien avec l'enfant" placeholder="Ex. : Oncle, Voisine" f={f} set={set} err={err} required />
                <Champ k="urgence_tel" label="Téléphone" type="tel" f={f} set={set} err={err} required />
              </>}
              {f.urgence_choix && <Champ k="urgence_tel2" label="Deuxième téléphone" type="tel" f={f} set={set} err={err} />}
            </Section>
            <Section n={7} title="Validation" testid="fiche-section-validation">
              <label className="flex cursor-pointer items-start gap-3 sm:col-span-2" data-error={!!err.confirme}>
                <input type="checkbox" data-testid="fiche-confirme" checked={f.confirme} onChange={(e) => set("confirme")(e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 accent-[#D4AF37]" />
                <span className="text-chalk">Je confirme l'exactitude des informations fournies.<span className="text-signal"> *</span>
                  {err.confirme && <span data-testid="fiche-confirme-error" className="mt-1 block text-xs text-signal">{err.confirme}</span>}</span>
              </label>
              <Champ k="date_fiche" label="Date du jour" type="date" f={f} set={set} err={err} required />
            </Section>
            {serverErr && <p data-testid="fiche-server-error" role="alert" className="rounded-xl border border-signal bg-signal/10 px-4 py-3 text-sm text-chalk">{serverErr}</p>}
            <button data-testid="fiche-submit" disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-full bg-lime px-6 py-4 font-semibold text-ink transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60">
              {busy && <Loader2 className="h-5 w-5 animate-spin" />}Envoyer la fiche
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
